// Patient status enum (mirrors the Xano `patient.status` value list). Changing a
// patient's status goes through the dedicated POST /patient/status/{id} endpoint
// (see functions/api/patients/status/[id].js), NOT the generic patient PATCH.
//
// Grouped + ordered: มีนัดแล้ว first, then Recalls, case types, then ยกเลิกเคส,
// and Error last.
//
// R48 is deliberately NOT here (removed 2026-09-09, and dropped from the Xano
// `patient.status` enum the same day). A 48h hold is a fact about ONE booking,
// not about the patient — a patient can hold one slot and have another confirmed,
// which a single-valued patient status cannot express. It lives on the
// appointment only: see data/appointmentStatuses.js.
//
// ตามเคสด่วน is retired the same way (Ram, 2026-09-19) — gone from the pickers.
// It is still in the Xano enum, so a patient already carrying it keeps it until
// staff pick something else; statusTone() shows it as a neutral slate pill.
// 🔁 A booking takes a patient OUT of their Recall queue (Xano trigger 2, from
// 2026-09-13): Error / empty / any `Recall*` → มีนัดแล้ว, with the overwritten
// Recall stashed in `patient.status_prev` and put BACK when the booking goes away.
// Nothing in the CRM writes `status_prev` — the trigger owns it; the appointment
// drawer only READS it, to tell staff what the status will fall back to. The
// ประเภทเคส group below is deliberately never promoted over.
export const STATUS_GROUPS = [
  { label: 'นัดหมาย', items: ['มีนัดแล้ว'] },
  {
    label: 'Recall',
    items: [
      'Recall 3 เดือน',
      'Recall 6 เดือน',
      'Recall GP',
      'Recall เคลียร์ช่องปาก',
      'Recall จัดฟันต่อเนื่อง',
      'Recall ติดเครื่องมือ',
      'Recall ปรับเครื่องมือ',
      'Recall ปรึกษาจัดฟัน',
      'Recall สปสช.',
    ],
  },
  { label: 'ประเภทเคส', items: ['ปกส', 'รากเทียม', 'Tooth Taxi'] },
  { label: 'ปิดเคส', items: ['ยกเลิกเคส'] },
  { label: 'ระบบ', items: ['Error'] },
]

export const PATIENT_STATUSES = STATUS_GROUPS.flatMap((g) => g.items)

// Tailwind classes per tone — { pill, dot }.
export const STATUS_TONES = {
  none:   { pill: 'bg-slate-100 text-slate-500 ring-slate-200',      dot: 'bg-slate-400' },
  green:  { pill: 'bg-emerald-50 text-emerald-700 ring-emerald-200', dot: 'bg-emerald-500' },
  yellow: { pill: 'bg-amber-50 text-amber-700 ring-amber-200',       dot: 'bg-amber-500' },
  orange: { pill: 'bg-orange-50 text-orange-700 ring-orange-200',    dot: 'bg-orange-500' },
  blue:   { pill: 'bg-blue-50 text-blue-700 ring-blue-200',          dot: 'bg-blue-500' },
  violet: { pill: 'bg-violet-50 text-violet-700 ring-violet-200',    dot: 'bg-violet-500' },
  teal:   { pill: 'bg-teal-50 text-teal-700 ring-teal-200',          dot: 'bg-teal-500' },
  slate:  { pill: 'bg-slate-100 text-slate-600 ring-slate-300',      dot: 'bg-slate-400' },
  // ยกเลิกเคส — red, but soft. Error below is the FILLED red, so the two never
  // read as the same thing.
  red:    { pill: 'bg-red-50 text-red-700 ring-red-200',             dot: 'bg-red-500' },
  // Error is loud — filled red.
  error:  { pill: 'bg-rose-600 text-white ring-rose-600',            dot: 'bg-white' },
}

/** Is this one of the Recall queue statuses? The name IS the rule — same test the
 *  Xano trigger uses (`|starts_with:"Recall"`). */
export const isRecallStatus = (s) => String(s ?? '').trim().startsWith('Recall')

/**
 * What Xano trigger 2 will write on `patient.status` — computed in the browser, so
 * the appointment drawer can SHOW the consequence of an unsaved edit without
 * asking the server and without writing anything.
 *
 * 🚨 A MIRROR, NOT A DECISION. The authority is table trigger 2 on table 20
 * (script: knowledge/xano/backups/trigger2_patient-status_2026-09-13_AFTER.xs).
 * Change one and you MUST change the other, or the drawer promises staff a status
 * the server does not deliver — which is worse than saying nothing at all.
 *
 *   current           the patient's status right now
 *   statusPrev        patient.status_prev — the stashed recall queue, if any
 *   hasBooking        will a qualifying future booking remain AFTER this save?
 *                     (caller computes it with hasFutureBooking() over the rows
 *                     it already has in memory — no request needed)
 *
 * Returns the status the patient will END UP with; === `current` means no change.
 */
export function predictPatientStatus({ current, statusPrev, hasBooking }) {
  const cur = String(current ?? '').trim()
  const prev = String(statusPrev ?? '').trim()
  // Demote: the last qualifying booking is gone. Fall back to the stashed recall
  // queue, or Error when there is nothing to go back to.
  if (cur === 'มีนัดแล้ว' && !hasBooking) return isRecallStatus(prev) ? prev : 'Error'
  // Promote: a booking exists and the patient is in a state a booking resolves.
  // ปกส / รากเทียม / Tooth Taxi / ยกเลิกเคส are case types and
  // closures, never promoted over.
  if (hasBooking && (cur === 'Error' || cur === '' || isRecallStatus(cur))) return 'มีนัดแล้ว'
  return cur
}

export function statusTone(status) {
  if (!status) return 'none'
  if (status === 'Error') return 'error'
  if (status === 'มีนัดแล้ว') return 'green'
  if (status === 'ยกเลิกเคส') return 'red'
  if (status === 'ปกส') return 'blue'
  if (status === 'รากเทียม') return 'violet'
  if (status === 'Tooth Taxi') return 'orange'
  if (status.startsWith('Recall')) return 'yellow'
  return 'slate'
}
